import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

const SESSION_CHECK_INTERVAL = 10_000; // 10 segundos
const SESSION_ID_KEY = 'kredit_session_id';

/**
 * Servicio que verifica periódicamente si la sesión sigue activa en el servidor.
 * Cuando otro dispositivo hace forceLogin, el servidor crea un nuevo sessionId.
 * Este servicio detecta el cambio y fuerza el logout automáticamente.
 */
@Injectable({
    providedIn: 'root'
})
export class SessionGuardService implements OnDestroy {
    private intervalId: any = null;
    private consecutiveFailures = 0;
    private static readonly MAX_FAILURES_BEFORE_LOGOUT = 2;

    constructor(private router: Router) { }

    /**
     * Guardar el sessionId en localStorage
     */
    static saveSessionId(sessionId: string): void {
        if (sessionId) {
            localStorage.setItem(SESSION_ID_KEY, sessionId);
        }
    }

    /**
     * Obtener el sessionId del localStorage
     */
    static getSessionId(): string | null {
        return localStorage.getItem(SESSION_ID_KEY);
    }

    /**
     * Iniciar el polling de verificación de sesión
     */
    start(): void {
        // Evitar iniciar múltiples intervalos
        if (this.intervalId) return;
        this.consecutiveFailures = 0;

        console.log('🛡️ [SESSION GUARD] Iniciado — verificando cada 10s');

        // NO hacer primera verificación inmediata para evitar race condition
        // El sessionId puede no estar sincronizado aún con el backend
        // Esperar el primer intervalo (10s) antes de verificar
        
        // Verificar periódicamente
        this.intervalId = setInterval(() => this.checkSession(), SESSION_CHECK_INTERVAL);
    }

    /**
     * Detener el polling
     */
    stop(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
            console.log('🛡️ [SESSION GUARD] Detenido');
        }
    }

    /**
     * Verificar si la sesión sigue activa
     */
    private async checkSession(): Promise<void> {
        const token = localStorage.getItem('x-token');
        const sessionId = SessionGuardService.getSessionId();

        if (!token || !sessionId) return;

        try {
            const res = await fetch(`${environment.BASE_LA}/api/auth/session-status`, {
                method: 'GET',
                headers: {
                    'Authorization': token,
                    'X-Session-Id': sessionId,
                },
            });

            if (res.status === 401) {
                this.consecutiveFailures++;
                console.log(`⚠️ [SESSION GUARD] Fallo ${this.consecutiveFailures}/${SessionGuardService.MAX_FAILURES_BEFORE_LOGOUT}`);

                if (this.consecutiveFailures >= SessionGuardService.MAX_FAILURES_BEFORE_LOGOUT) {
                    console.log('⚠️ [SESSION GUARD] Sesión invalidada — cerrando sesión...');
                    this.stop();
                    sessionStorage.setItem('sessionClosedMessage', 'Tu sesión fue cerrada porque iniciaste sesión en otro dispositivo');
                    this.router.navigate([environment.logoutRoute]);
                }
            } else {
                this.consecutiveFailures = 0;
            }
        } catch {
            // Error de red — no hacer nada, reintentar en el próximo ciclo
        }
    }

    ngOnDestroy(): void {
        this.stop();
    }
}
